import React, { useEffect, useRef } from 'react';
import './HabitList.css';
import HabitItem from './HabitItem';
import { createConfettiEffect } from '../utils/feedback';

const HabitList = ({ habits, onHabitComplete, currentDate }) => {
  const completionCardRef = useRef(null);
  // Group habits by time of day and category
  const groupedHabits = habits.reduce((groups, habit) => {
    const timeOfDay = habit.timeOfDay || 'all';
    if (!groups[timeOfDay]) {
      groups[timeOfDay] = [];
    }
    groups[timeOfDay].push(habit);
    return groups;
  }, {});

  const timeOfDayOrder = ['morning', 'all', 'evening'];
  const timeOfDayLabels = {
    morning: '🌅 Morning Routine',
    all: '💫 Anytime',
    evening: '🌙 Evening Routine'
  };

  const getCompletionStats = () => {
    const total = habits.length;
    const completed = habits.filter(habit => habit.isCompleted).length;
    return { total, completed };
  };

  const stats = getCompletionStats();

  // Trigger confetti when all habits are completed
  useEffect(() => {
    if (stats.completed === stats.total && stats.total > 0 && completionCardRef.current) {
      const timer = setTimeout(() => {
        createConfettiEffect(completionCardRef.current);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [stats.completed, stats.total]);

  if (habits.length === 0) {
    return (
      <div className="habit-list-empty">
        <div className="empty-state">
          <span className="empty-icon">🌸</span>
          <h3>No habits scheduled for this day</h3>
          <p>Your skin gets a rest day! Check back tomorrow for your routine.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="habit-list">
      <div className="habit-list-header">
        <h3>Today's Skincare Routine</h3>
        <div className="completion-summary">
          <span className="completion-count">
            {stats.completed} of {stats.total} completed
          </span>
          {stats.completed === stats.total && stats.total > 0 && (
            <span className="completion-celebration">🎉</span>
          )}
        </div>
      </div>

      <div className="habit-groups">
        {timeOfDayOrder.map(timeOfDay => {
          const habitsForTime = groupedHabits[timeOfDay];
          if (!habitsForTime || habitsForTime.length === 0) return null;

          return (
            <div key={timeOfDay} className="habit-group">
              <h4 className="group-title">{timeOfDayLabels[timeOfDay]}</h4>
              <div className="habit-items">
                {habitsForTime.map(habit => (
                  <HabitItem
                    key={habit.id}
                    habit={habit}
                    onComplete={() => onHabitComplete(habit.id)}
                    currentDate={currentDate}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {stats.completed === stats.total && stats.total > 0 && (
        <div className="daily-completion-message">
          <div className="completion-celebration-card" ref={completionCardRef}>
            <span className="celebration-emoji">✨</span>
            <h4>Perfect Day Complete!</h4>
            <p>You've completed all your skincare habits for today. Your skin will thank you!</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default HabitList;
